import { useEffect, useMemo, useRef, useState } from 'react';
import Particles from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';
import { loadSnowPreset } from '@tsparticles/preset-snow';
import { loadConfettiPreset } from '@tsparticles/preset-confetti';
import type { Engine, Container } from '@tsparticles/engine';
import type { VFXConfig } from '../types/vfxConfig';
import { getVFXConfig } from '../config/vfxEffects';

// Global engine — loaded once for the lifetime of the app
let globalEngine: Engine | null = null;
let engineInitializing = false;
let engineInitialized = false;

function safeDestroy(container: { destroy(): void }): void {
  try {
    container.destroy();
  } catch (e) {
    console.warn('[VFXCanvas] container.destroy() failed:', e);
  }
}

async function initializeEngine(): Promise<Engine | null> {
  if (engineInitialized && globalEngine) return globalEngine;
  if (engineInitializing) {
    while (engineInitializing) await new Promise(r => setTimeout(r, 100));
    return globalEngine;
  }

  engineInitializing = true;
  try {
    const { tsParticles } = await import('@tsparticles/engine');
    globalEngine = tsParticles;
    await loadSlim(tsParticles);
    try { await loadSnowPreset(tsParticles); } catch (e) { console.warn('[VFXCanvas] snow preset:', e); }
    try { await loadConfettiPreset(tsParticles); } catch (e) { console.warn('[VFXCanvas] confetti preset:', e); }
    engineInitialized = true;
    return tsParticles;
  } catch (e) {
    console.warn('[VFXCanvas] engine init failed:', e);
    return null;
  } finally {
    engineInitializing = false;
  }
}

async function loadPlugins(engine: Engine, effectType: string): Promise<void> {
  try {
    if (effectType === 'snow') {
      const { loadImageShape } = await import('@tsparticles/shape-image');
      await loadImageShape(engine);
    }
    if (effectType === 'fireworks' || effectType === 'emitters') {
      const { loadEmittersPlugin } = await import('@tsparticles/plugin-emitters');
      await loadEmittersPlugin(engine);
    }
    if (effectType === 'absorbers') {
      const { loadAbsorbersPlugin } = await import('@tsparticles/plugin-absorbers');
      await loadAbsorbersPlugin(engine);
    }
    if (effectType === 'polygons') {
      const { loadPolygonShape } = await import('@tsparticles/shape-polygon');
      await loadPolygonShape(engine);
    }
  } catch (e) {
    console.warn('[VFXCanvas] plugin load failed:', e);
  }
}

function destroyAllContainers(): void {
  if (!globalEngine) return;
  globalEngine.dom().forEach(c => safeDestroy(c));
}

interface VFXCanvasProps {
  readonly config: VFXConfig | null;
}

export default function VFXCanvas({ config }: VFXCanvasProps) {
  const [options, setOptions] = useState<unknown>(undefined);
  const [isEngineReady, setIsEngineReady] = useState(false);
  const previousConfigRef = useRef<string | null>(null);
  const pluginsLoadedRef = useRef<Set<string>>(new Set());
  const containerRef = useRef<Container | null>(null);
  const instanceIdRef = useRef<string>(`vfx-${Date.now()}-${Math.random()}`);

  useEffect(() => {
    initializeEngine().then(engine => { if (engine) setIsEngineReady(true); });
    return () => { destroyAllContainers(); };
  }, []);

  const configKey = useMemo(() => {
    if (!config?.enabled || !config.type || !config.intensity) return null;
    return `${config.type}-${config.intensity}`;
  }, [config]);

  // Destroy containers when config changes
  useEffect(() => {
    if (configKey !== previousConfigRef.current) {
      destroyAllContainers();
      containerRef.current = null;
    }
  }, [configKey]);

  // Load particles options when config changes
  useEffect(() => {
    if (!config?.enabled || !config.type || !config.intensity) {
      destroyAllContainers();
      setOptions(undefined);
      previousConfigRef.current = null;
      return;
    }
    if (configKey === previousConfigRef.current) return;

    destroyAllContainers();
    previousConfigRef.current = configKey;

    getVFXConfig(config.type, config.intensity)
      .then(setOptions)
      .catch(() => setOptions(undefined));
  }, [config, configKey]);

  // Load plugins on-demand per effect type
  useEffect(() => {
    if (!isEngineReady || !globalEngine || !config?.enabled || !config.type) return;
    if (pluginsLoadedRef.current.has(config.type)) return;
    loadPlugins(globalEngine, config.type).then(() => {
      pluginsLoadedRef.current.add(config.type!);
    });
  }, [isEngineReady, config?.type, config?.enabled]);

  // Destroy stale duplicate containers when config/engine state changes
  useEffect(() => {
    if (!isEngineReady || !config?.enabled || !globalEngine) return;
    const containers = globalEngine.dom();
    if (containers.length > 1) {
      containers.forEach(c => { if (c !== containerRef.current) safeDestroy(c); });
    }
  }, [isEngineReady, config?.enabled, configKey]);

  // When a new container mounts, discard any stale ones and store the reference
  const particlesLoaded = async (container?: Container) => {
    if (!container || !globalEngine) return;
    globalEngine.dom().forEach(existing => {
      if (existing !== container) safeDestroy(existing);
    });
    containerRef.current = container;
  };

  if (!isEngineReady || !config?.enabled || !config.type || !config.intensity || !options) {
    return null;
  }

  return (
    <div
      id="vfx-container"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 10,
        pointerEvents: 'none',
        transition: 'opacity 0.5s ease',
        backgroundColor: 'transparent',
      }}
      aria-hidden="true"
    >
      <Particles
        key={`${configKey}-${instanceIdRef.current}`}
        id={`vfx-particles-${instanceIdRef.current}`}
        options={options as Record<string, unknown>}
        particlesLoaded={particlesLoaded}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
}
